import { Component } from '@verndale/core';
import { isMobile } from '../helpers';
import Swiper, { Pagination } from 'swiper';
import { KEYS } from '../constants';
import _debounce from 'lodash.debounce';
import { focusableElements } from '../helpers';

Swiper.use([Pagination]);

class Module extends Component {
  setupDefaults() {
    this.dom = {
      $container: this.el._$('.swiper-container'),
      $wrapper: this.el._$('.swiper-wrapper'),
      $pagination: this.el._$('.swiper-pagination'),
      $thumbs: this.el._$$('.swiper-slide')
    };

    this.dotLabel = this.el.dataset.dotLabel;
    this.currentFocusIndex = -1;
    this.focusables = document._$$(focusableElements);

    if (isMobile()) {
      this.initSlider();
      this.makeBulletsAccessible();
    }
  }

  addListeners() {
    window._on('mousedown', this.handleMouseDown.bind(this));
    window._on('resize', _debounce(this.handleWindowResize.bind(this), 250));
  }

  initSlider() {
    const that = this;
    let thumbsPerPage = this.thumbsPerPage;
    if (!thumbsPerPage) thumbsPerPage = 4;

    this.slider = new Swiper(this.dom.$container, {
      centeredSlides: false,
      grid: {
        rows: 2
      },
      keyboard: {
        enabled: true
      },
      slidesPerColumn: 2,
      slidesPerColumnFill: 'row',
      slidesPerGroup: 2,
      slidesPerView: 2,
      spaceBetween: 30,
      on: {
        init(swiper) {
          if (swiper.slides.length <= thumbsPerPage) {
            that.dom.$pagination.style.display = 'none';
            if (swiper.slides.length <= 2) that.dom.$wrapper.style.display = 'flex';
          }

          swiper.el._on('keydown', e => { that.handleSwiperKeyDown(e, swiper) });
        }
      },
      pagination: {
        el: this.dom.$pagination,
        clickable: true
      },
      watchOverflow: true
    });
  }

  // The Swiper JS library is not 100% accessible, so we have to
  // apply some custom workarounds
  makeBulletsAccessible() {
    this.dom.$bullets = this.el._$$('.swiper-pagination-bullet');

    this.dom.$bullets?.forEach(($bullet, idx) => {
      if (this.dotLabel) $bullet.setAttribute('aria-label', this.dotLabel.concat(idx));
      $bullet.setAttribute('role', 'button');
      $bullet.setAttribute('tabindex', 0);
      $bullet._on('keydown', this.handleBulletKeyDown.bind(this));
    });

    this.dom.$buttons = [];

    this.dom.$thumbs.forEach($thumb => {
      // set same order on focus elements to avoid chaotic tabbing
      // (this is being set inline by SwiperJS)
      const btn = $thumb._$('.gallery-row__content-button');
      const thumbOrder = this.getThumbOrder($thumb);
      btn.style.order = thumbOrder;
      this.dom.$buttons.push(btn);
    });

    this.dom.$buttons.sort((a, b) => {
      return parseInt(a.style.order) - parseInt(b.style.order);
    });
  }

  // this helper gets the actual visual order of a thumbnail, given that Swiper JS
  // can make it different to the markup order, especially with grids, creating then chaotic tabbing
  getThumbOrder(thumb) {
    return parseInt(thumb.getAttribute('style').toString().split('order:')[1].split(';')[0]);
  }

  // layout switch helper - reset inline styles applied by Swiper JS
  resetSwiperStyles() {
    this.dom.$container.removeAttribute('style');
    this.dom.$wrapper.removeAttribute('style');

    this.dom.$thumbs.forEach($thumb => {
      $thumb.removeAttribute('style');
    })
  }

  handleBulletKeyDown(e) {
    if (e.keyCode !== KEYS.ENTER && e.keyCode !== KEYS.SPACE) return;
    e.target.click();
  }

  handleMouseDown(e) {
    this.currentFocusIndex = -1;
  }

  handleSwiperKeyDown(e, swiper) {
    e.preventDefault();
    const isTab = e.keyCode === KEYS.TAB;
    if (!isTab) return;

    const isTabBack = isTab && e.shiftKey;
    const activeElementOrder = this.getThumbOrder(document.activeElement);
    const indexInFocusables = this.focusables.indexOf(document.activeElement);

    let nextFocusable;

    // as we are listening for keydown events on the gallery row element
    // (and not in the whole module nor document) this will only happen
    // the first time a user makes focus into the first thumbnail button
    if (this.currentFocusIndex < 0 && activeElementOrder >= 0) {
      this.currentFocusIndex = 0;
    }

    if (!isTabBack && this.currentFocusIndex < this.dom.$buttons.length) {
      this.currentFocusIndex++;
    } else if (this.currentFocusIndex >= 0) {
      this.currentFocusIndex--;
    }

    if (this.currentFocusIndex >= this.dom.$buttons.length) {
      // after hitting tab on the last thumbnail button
      nextFocusable = this.focusables[indexInFocusables + 1];
      document.activeElement.blur();
    } else if (this.currentFocusIndex < 0) {
      // after hitting shift+tab on the first thumbnail button
      nextFocusable = this.focusables[indexInFocusables - 1];
      document.activeElement.blur();
    } else {
      this.dom.$buttons[this.currentFocusIndex]?.focus();

      if (this.dom.$thumbs.length > 4) {
        // reset default browser content displacement after user tabbing
        this.dom.$wrapper.parentElement.scrollLeft = 0;

        const slideIndex = this.getThumbOrder(document.activeElement);
        const page = Math.floor(slideIndex / 4); // 4 thumbnails per page

        // We have to check it this way because swiper.realIndex
        // and swiper.realIndex are not reliable (this is a known issue)
        const activeBullet = this.el._$('.swiper-pagination-bullet-active');
        const swiperIndex = this.dom.$bullets.indexOf(activeBullet);

        if (page !== swiperIndex) isTabBack ? swiper.slidePrev(0) : swiper.slideNext(0);
      }
    }

    if (nextFocusable) nextFocusable.focus();
  }

  // we use this to show the Swiper only on mobile layouts (<768px wide)
  handleWindowResize() {
    if (isMobile() && !this.slider) {
      this.currentFocusIndex = -1;
      this.initSlider();
      if (!this.dom.$bullets) this.makeBulletsAccessible();
    } else if (!isMobile() && this.slider) {
      this.slider.destroy();
      this.slider = null;
      this.resetSwiperStyles();
    }
  }
}

export default Module;
